import { GetScheduleFormFormsRequestDTO, GetScheduleFormsRequestDTO, GetSchedulesRequestDTO, RecordState } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const scheduleKeys = {
  all: ['schedules'] as const,
  detail: (scheduleId: string | undefined, isAWA: boolean) => [...scheduleKeys.all, 'detail', scheduleId, isAWA] as const,
  infinite: (filters?: GetSchedulesRequestDTO['filters'], orderBy?: GetSchedulesRequestDTO['orderBy']) =>
    [...scheduleKeys.all, 'infinite', filters, orderBy] as const,
};

export const scheduleFormKeys = {
  all: ['schedule-forms'] as const,
  detail: (scheduleFormId: string | undefined, isAWA: boolean) => [...scheduleFormKeys.all, 'detail', scheduleFormId, isAWA] as const,
  revisions: (scheduleFormId: string | undefined, isAWA: boolean) => [...scheduleFormKeys.all, 'revisions', scheduleFormId, isAWA] as const,
  formByRevisionId: (formRevisionId: string | null | undefined, isAWA: boolean) => [...scheduleFormKeys.all, 'form', formRevisionId, isAWA] as const,
  formsSelect: () => [...scheduleFormKeys.all, 'forms-select'] as const,
  formsInfinite: (filters?: GetScheduleFormFormsRequestDTO['filters'], orderBy?: GetScheduleFormFormsRequestDTO['orderBy']) =>
    [...scheduleFormKeys.all, 'forms-infinite', filters, orderBy] as const,
  infinite: (
    filters?: GetScheduleFormsRequestDTO['filters'],
    groupBy?: GetScheduleFormsRequestDTO['groupBy'],
    orderBy?: GetScheduleFormsRequestDTO['orderBy'],
  ) => [...scheduleFormKeys.all, 'infinite', filters, groupBy, orderBy] as const,
};

export const unitsSelectKeys = {
  all: ['units-select'] as const,
  list: (limit: number) => [...unitsSelectKeys.all, limit] as const,
};

export const getScheduleByIdQueryOptions = ({ scheduleId, isAWA = false }: { scheduleId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: scheduleKeys.detail(scheduleId, isAWA),
    queryFn: async () => {
      if (!scheduleId) return null;
      return (await (await (isAWA ? AWAApiV2Service : ApiService).GetScheduleById({ scheduleId })).json()).schedule;
    },
    enabled: !!scheduleId,
  });
};

export const getScheduleFormQueryOptions = ({ scheduleFormId, isAWA = false }: { scheduleFormId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: scheduleFormKeys.detail(scheduleFormId, isAWA),
    queryFn: async () => {
      if (!scheduleFormId) return null;
      return (await (await (isAWA ? AWAApiV2Service : ApiService).GetScheduleFormById({ scheduleFormId })).json()).scheduleForm;
    },
    enabled: !!scheduleFormId,
  });
};

export const getScheduleFormRevisionsQueryOptions = ({ scheduleFormId, isAWA = false }: { scheduleFormId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: scheduleFormKeys.revisions(scheduleFormId, isAWA),
    queryFn: async () => {
      if (!scheduleFormId) return null;
      return (await (await (isAWA ? AWAApiV2Service : ApiService).GetScheduleFormRevisions({ scheduleFormId })).json()).revisions;
    },
    enabled: !!scheduleFormId,
  });
};

export const getScheduleFormFormQueryOptions = ({ formRevisionId, isAWA = false }: { formRevisionId: string | null | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: scheduleFormKeys.formByRevisionId(formRevisionId, isAWA),
    queryFn: async () => {
      if (!formRevisionId) return null;
      return (await (await (isAWA ? AWAApiV2Service : ApiService).GetFormRevisionById({ formRevisionId })).json()).formRevision;
    },
    enabled: !!formRevisionId,
  });
};

export const getScheduleFormFormsSelectQueryOptions = () => {
  return queryOptions({
    queryKey: scheduleFormKeys.formsSelect(),
    queryFn: async () => (await ApiService.GetScheduleFormForms({ filters: { state: RecordState.Active } })).json(),
  });
};

export const getUnitsSelectQueryOptions = ({ limit = 100 }: { limit?: number } = {}) => {
  return queryOptions({
    queryKey: unitsSelectKeys.list(limit),
    queryFn: async () => (await ApiService.GetUnits({ limit, filters: { state: RecordState.Active } })).json(),
  });
};

export const getSchedulesInfiniteQueryOptions = ({
  filters,
  orderBy,
  enabled = true,
}: {
  filters?: GetSchedulesRequestDTO['filters'];
  orderBy?: GetSchedulesRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: scheduleKeys.infinite(filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) => await (await ApiService.GetSchedules({ limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getScheduleFormFormsInfiniteQueryOptions = ({
  filters,
  orderBy,
  enabled = true,
}: {
  filters?: GetScheduleFormFormsRequestDTO['filters'];
  orderBy?: GetScheduleFormFormsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: scheduleFormKeys.formsInfinite(filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetScheduleFormForms({ limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getScheduleFormsInfiniteQueryOptions = ({
  filters,
  groupBy,
  orderBy,
  enabled = true,
}: {
  filters?: GetScheduleFormsRequestDTO['filters'];
  groupBy?: GetScheduleFormsRequestDTO['groupBy'];
  orderBy?: GetScheduleFormsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: scheduleFormKeys.infinite(filters, groupBy, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetScheduleForms({ limit: 30, cursors: pageParam, filters, groupBy, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};
